<template>
  <div>
    <PageTitle title="Request Migration" />

    <Alert v-if="errors" :message="errors" variant="danger" @close="clearErrors" />
    <div class="migration-container">
      <div v-if="loadingSettings" class="loading-container">
        <Icon icon="loader" color="alpha" />
      </div>
      <template v-else>
        <div class="migration-summary">
          <div
            class="step-container"
            :class="{ done: step !== 'contact', current: step === 'contact' }"
          >
            <div class="step-check">
              <div class="inner">
                <Icon
                  icon="checkmark"
                  color="gamma"
                  :size="10"
                  class="check-icon"
                  v-if="step !== 'contact'"
                />
              </div>
            </div>
            <div class="step-description">
              <div class="step-title">Step 1</div>
              <div class="step-subtitle">Contact Information</div>
            </div>
          </div>
          <div
            class="step-container"
            :class="{ done: step === 'confirm', current: step === 'site' }"
          >
            <div class="step-check">
              <div class="inner">
                <Icon
                  icon="checkmark"
                  color="gamma"
                  :size="10"
                  class="check-icon"
                  v-if="step === 'confirm'"
                />
              </div>
            </div>
            <div class="step-description">
              <div class="step-title">Step 2</div>
              <div class="step-subtitle">Add Website(s)</div>
              <template v-if="migration.sites.length">
                <div class="step-site" v-for="site in migration.sites" :key="site.index">
                  <div>{{ site.website }}</div>
                  <div class="site-edit">
                    <a href="#" @click="loadSite(site)">
                      <Icon icon="edit" color="gamma" :size="16" />
                    </a>
                    <a href="#" @click="deleteSite(site)">
                      <Icon icon="trash" color="gamma" :size="16" />
                    </a>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <div class="step-container" :class="{ current: step === 'confirm' }">
            <div class="step-check"><div class="inner"></div></div>
            <div class="step-description">
              <div class="step-title">Step 3</div>
              <div class="step-subtitle">Review &amp; Submit</div>
            </div>
          </div>
        </div>
        <div class="migration-body">
          <div class="userinfo-form" v-if="step === 'contact'">
            <div class="title">Contact Information</div>
            <div class="content">
              <div class="row">
                <div class="col">
                  <FormInput
                    name="name"
                    v-model="migration.name"
                    testId="name-input"
                    :label="`${$t('global.name')} *`"
                    :errors="validation.fields.name.errors"
                    :dirty="validation.fields.name.dirty"
                    :disabled="loading"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormInput
                    name="email"
                    v-model="migration.email"
                    testId="email-input"
                    :label="`${$t('global.email')} *`"
                    :errors="validation.fields.email.errors"
                    :dirty="validation.fields.email.dirty"
                    :disabled="loading"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <FormInput
                    name="phone"
                    v-model="migration.phone"
                    testId="phone-input"
                    :label="`${$t('global.phone')} *`"
                    :errors="validation.fields.phone.errors"
                    :dirty="validation.fields.phone.dirty"
                    :disabled="loading"
                  />
                </div>
              </div>
              <div class="content-action">
                <Btn @click="goToSite">Next</Btn>
              </div>
            </div>
          </div>
          <div class="site-form" v-if="step === 'site'">
            <div class="title">Contact Information</div>
            <div class="content">
              <div class="row">
                <div class="col">
                  <FormInput
                    name="website"
                    v-model="fields2.website"
                    testId="website-input"
                    label="Website"
                    :errors="validation2.fields.website.errors"
                    :dirty="validation2.fields.website.dirty"
                    :disabled="loading"
                    prepend="https://"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="migrate_from"
                    v-model="fields2.migrate_from"
                    label="Where is the website moving from?"
                    :errors="validation2.fields.migrate_from.errors"
                    :dirty="validation2.fields.migrate_from.dirty"
                  >
                    <option value="">- Select an option -</option>
                    <option value="Nexcess">Nexcess</option>
                    <option value="Liquid Web">Liquid Web</option>
                    <option value="WP Engine">WP Engine</option>
                    <option value="Kinsta">Kinsta</option>
                    <option value="Pagely">Pagely</option>
                    <option value="Pantheon">Pantheon</option>
                    <option value="Cloudways">Cloudways</option>
                    <option value="SiteGround">SiteGround</option>
                    <option value="FlyWheel">FlyWheel</option>
                    <option value="GoDaddy">GoDaddy</option>
                    <option value="Bluehost">Bluehost</option>
                    <option value="HostGator">HostGator</option>
                    <option value="DreamHost">DreamHost</option>
                    <option value="A2 Hosting">A2 Hosting</option>
                    <option value="InMotion">InMotion</option>
                    <option value="Namecheap">Namecheap</option>
                    <option value="Google Cloud Platform">Google Cloud Platform</option>
                    <option value="Amazon AWS">Amazon AWS</option>
                    <option value="DigitalOcean">DigitalOcean</option>
                    <option value="Vultr">Vultr</option>
                    <option value="Linode">Linode</option>
                    <option value="Other">Other</option>
                  </FormSelect>
                </div>
                <div class="col-12 col-sm-6">
                  <FormInput
                    name="migrate_from_other"
                    v-model="fields2.migrate_from_other"
                    testId="migrate_from_other-input"
                    label="Tell us where your website is moving from."
                    :errors="validation2.fields.migrate_from_other.errors"
                    :dirty="validation2.fields.migrate_from_other.dirty"
                    :disabled="loading"
                    v-if="fields2.migrate_from === 'Other'"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="application_type"
                    v-model="fields2.application_type"
                    label="What type of CMS or Website is it that will be migrated over?"
                    :errors="validation2.fields.application_type.errors"
                    :dirty="validation2.fields.application_type.dirty"
                  >
                    <option value="">- Select an option -</option>
                    <option value="WordPress">WordPress</option>
                    <option value="WooCommerce">WooCommerce</option>
                    <option value="Magento">Magento</option>
                    <option value="Drupal">Drupal</option>
                    <option value="Joomla">Joomla</option>
                    <option value="Craft CMS">Craft CMS</option>
                    <option value="ExpressionEngine">ExpressionEngine</option>
                    <option value="Sylius">Sylius</option>
                    <option value="Mixed">Mixed</option>
                    <option value="Other">Other</option>
                  </FormSelect>
                </div>
                <div class="col-12 col-sm-6">
                  <FormInput
                    name="application_type_other"
                    v-model="fields2.application_type_other"
                    testId="application_type_other-input"
                    label="What is the name of the CMS or website platform?"
                    :errors="validation2.fields.application_type_other.errors"
                    :dirty="validation2.fields.application_type_other.dirty"
                    :disabled="loading"
                    v-if="fields2.application_type === 'Other'"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="hosting_access"
                    v-model="fields2.hosting_access"
                    label="Hosting Access"
                    :errors="validation2.fields.hosting_access.errors"
                    :dirty="validation2.fields.hosting_access.dirty"
                  >
                    <option value="">- Select an option -</option>
                    <option value="SSH Creds">SSH Creds</option>
                    <option value="Panel Creds">Panel Creds</option>
                    <option
                      value="Admin User Creds"
                      v-if="['WordPress', 'WooCommerce'].includes(fields2.application_type)"
                      >Admin User Creds</option
                    >
                  </FormSelect>
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="php_version"
                    v-model="fields2.php_version"
                    label="What version of PHP does your site use?"
                    :errors="validation2.fields.php_version.errors"
                    :dirty="validation2.fields.php_version.dirty"
                  >
                    <option value="7.4">7.4</option>
                    <option value="7.3">7.3</option>
                    <option value="7.2">7.2</option>
                    <option value="7.1">7.1</option>
                    <option value="7.0">7.0</option>
                    <option value="5.6 or lower">5.6 or lower</option>
                  </FormSelect>
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="multisite"
                    v-model="fields2.multisite"
                    :label="
                      fields2.application_type === 'Magento'
                        ? 'Is this a multistore?'
                        : 'Is this a multisite?'
                    "
                    :errors="validation2.fields.multisite.errors"
                    :dirty="validation2.fields.multisite.dirty"
                    v-if="
                      ['WordPress', 'WooCommerce', 'Magento'].includes(fields2.application_type)
                    "
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </FormSelect>
                </div>
                <div class="col-12 col-sm-6">
                  <FormTextArea
                    name="multisite_configuration"
                    v-model="fields2.multisite_configuration"
                    label="Multisite configuration"
                    :errors="validation2.fields.multisite_configuration.errors"
                    :dirty="validation2.fields.multisite_configuration.dirty"
                    v-if="
                      fields2.multisite === 'Yes' &&
                      ['WordPress', 'WooCommerce', 'Magento'].includes(fields2.application_type)
                    "
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="migrate_email"
                    v-model="fields2.migrate_email"
                    label="Migrate email?"
                    :errors="validation2.fields.migrate_email.errors"
                    :dirty="validation2.fields.migrate_email.dirty"
                    v-if="['Nexcess', 'Liquid Web'].includes(fields2.migrate_from)"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </FormSelect>
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="redis"
                    v-model="fields2.redis"
                    label="Redis?"
                    :errors="validation2.fields.redis.errors"
                    :dirty="validation2.fields.redis.dirty"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </FormSelect>
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6">
                  <FormSelect
                    name="outside_files"
                    v-model="fields2.outside_files"
                    label="Outside files?"
                    :errors="validation2.fields.outside_files.errors"
                    :dirty="validation2.fields.outside_files.dirty"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </FormSelect>
                </div>
                <div class="col-12 col-sm-6">
                  <FormTextArea
                    name="outside_files_locations"
                    v-model="fields2.outside_files_locations"
                    label="Outside file locations"
                    :errors="validation2.fields.outside_files_locations.errors"
                    :dirty="validation2.fields.outside_files_locations.dirty"
                    v-if="fields2.outside_files === 'Yes'"
                  />
                </div>
              </div>
              <div class="content-action">
                <!-- <Btn @click="test" variant="secondary">Test</Btn> -->
                <Btn @click="goToContact" variant="secondary" type="button" class="mr-2">Back</Btn>
                <Btn @click="addSite" variant="primaryOutline" type="button" class="mr-2"
                  >Add Another Site</Btn
                >
                <Btn @click="addSiteAndConfirm" type="button" variant="primary">Review</Btn>
              </div>
            </div>
          </div>
          <div class="summary-form" v-if="step === 'confirm'">
            <div class="title">Review Migration Request</div>
            <div class="content">
              <div class="contact-summary mb-4">
                <div><strong>Name:</strong> {{ migration.name }}</div>
                <div><strong>Email:</strong> {{ migration.email }}</div>
                <div><strong>Phone:</strong> {{ migration.phone }}</div>
              </div>
              <div class="row">
                <div class="col-12 col-sm-6" v-for="site in getSummaryInfo.sites" :key="site.index">
                  <div class="site-summary">
                    <div v-for="(line, l) in site.lines" :key="l">
                      <strong>{{ line.label }}:</strong> {{ line.content }}
                    </div>
                    <div class="site-actions">
                      <a href="#" @click="loadSite(site.site)">
                        <Icon icon="edit" color="gamma" :size="16" />
                      </a>
                      <a href="#" @click="deleteSite(site.site)">
                        <Icon icon="trash" color="gamma" :size="16" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="content-action">
                <Btn
                  @click="goToLastSite"
                  variant="secondary"
                  class="mr-2"
                  type="button"
                  :loading="loading"
                  >Back</Btn
                >
                <Btn @click="submitTicket" variant="primary" type="button" :loading="loading"
                  >Submit</Btn
                >
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import { migrationContact, migrationSite } from "leatherman-js/utils/validations/forms";
import { MigrationForm, MigrationFormSite } from "leatherman-js/models";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import FormSelect from "@/components/display/form/FormSelect";
import FormTextArea from "@/components/display/form/FormTextArea";
import Icon from "@/components/display/icon/Icon";
import PageTitle from "@/components/display/PageTitle";
import store from "@/store";
import useValidator from "@/utils/useValidator";

export default {
  name: "RequestMigrationPage",
  components: {
    Alert,
    Btn,
    // Card,
    Icon,
    // PageTitle,
    FormInput,
    FormSelect,
    FormTextArea,
    PageTitle,
  },
  setup(props, { root }) {
    const { validator } = useValidator();

    const client = computed(() => {
      return store.state.auth.client;
    });

    const errors = ref(null);
    const loading = ref(false);
    const loadingSettings = ref(false);

    const step = ref("contact"); // contact, site, confirm
    // const siteArray = ref([]);
    const migration = reactive(new MigrationForm());

    // const fields = reactive({
    //   name: null,
    //   email: null,
    //   phone: null,
    // });
    const validation = validator(
      migration,
      migrationContact,
      {
        email: {
          disableAutoDirty: true,
        },
        phone: {
          disableAutoDirty: true,
        },
      },
      {
        name: {
          required: "Name is required",
        },
        email: {
          email: "Email must be a properly formatted email address",
          required: "Email is required",
        },
        phone: {
          required: "Phone is required",
          phone: "Phone must be a 10-digit number",
        },
      }
    );

    // const siteIndex = ref(1);
    const fields2Default = new MigrationFormSite(1);
    const fields2 = reactive(new MigrationFormSite(1));
    const validation2 = validator(fields2, migrationSite, {}, {});

    const goToSite = () => {
      validation
        .validate()
        .then(() => {
          step.value = "site";
        })
        .catch(() => {});
    };

    const goToContact = () => {
      step.value = "contact";
    };

    const addSite = (action) => {
      validation2
        .validate()
        .then(() => {
          // siteArray.value.push({ ...fields2 });
          migration.addOrUpdateSite({ ...fields2 });
          // siteIndex.value = siteIndex.value + 1;

          fields2.index = migration.nextSiteIndex();
          fields2.website = fields2Default.website;
          fields2.migrate_from = fields2Default.migrate_from;
          fields2.migrate_from_other = fields2Default.migrate_from_other;
          fields2.hosting_access = fields2Default.hosting_access;
          fields2.application_type = fields2Default.application_type;
          fields2.application_type_other = fields2Default.application_type_other;
          fields2.php_version = fields2Default.php_version;
          fields2.multisite = fields2Default.multisite;
          fields2.multisite_configuration = fields2Default.multisite_configuration;
          fields2.migrate_email = fields2Default.migrate_email;
          fields2.outside_files = fields2Default.outside_files;
          fields2.outside_files_locations = fields2Default.outside_files_locations;
          fields2.redis = fields2Default.redis;

          setTimeout(() => {
            validation2.reset();
          }, 10);

          action();
        })
        .catch(() => {});
      // validate site form
      // push site fields to array of sites
      // reset site form to defaults
    };

    const addSiteAndConfirm = () => {
      if (migration.sites.length && !fields2.website) {
        step.value = "confirm";
      } else {
        addSite(() => {
          step.value = "confirm";
        });
      }
    };

    const test = () => {
      validation2.reset();
    };

    const getBody = () => {
      migration.getBody();
    };

    const goToLastSite = () => {
      const siteToLoad = migration.getLastSite();
      loadSite(siteToLoad);
      step.value = "site";
    };

    const loadSite = (siteLoad) => {
      step.value = "site";
      fields2.index = siteLoad.index;
      fields2.website = siteLoad.website;
      fields2.migrate_from = siteLoad.migrate_from;
      fields2.migrate_from_other = siteLoad.migrate_from_other;
      fields2.hosting_access = siteLoad.hosting_access;
      fields2.application_type = siteLoad.application_type;
      fields2.application_type_other = siteLoad.application_type_other;
      fields2.php_version = siteLoad.php_version;
      fields2.multisite = siteLoad.multisite;
      fields2.multisite_configuration = siteLoad.multisite_configuration;
      fields2.migrate_email = siteLoad.migrate_email;
      fields2.outside_files = siteLoad.outside_files;
      fields2.outside_files_locations = siteLoad.outside_files_locations;
      fields2.redis = siteLoad.redis;
    };

    const deleteSite = (siteDelete) => {
      migration.deleteSite(siteDelete);
    };

    // const getLines = (site) => {
    //   return fields2Default.getLines(site);
    // };

    const getSummaryInfo = computed(() => {
      return migration.getLines();
    });

    const normalTicketPriority = computed(() => {
      return (
        store.state.tickets.ticketOptions?.priority?.find((p) => p.identity === "Normal")?.id || 0
      );
    });

    const clearErrors = () => {
      errors.value = null;
    };

    const submitTicket = () => {
      loading.value = true;
      errors.value = null;
      const ticketData = {
        subject: `Migration for Client: ${client.value.id}`,
        body: migration.getBody(client.value.id),
        department: process.env.VUE_APP_SUPPORT_MIGRATION_DEPARTMENT,
        priority: normalTicketPriority.value,
      };

      store
        .dispatch("tickets/create", ticketData)
        .then((resp) => {
          root.$router.push({ name: "ticket-detail", params: { id: resp.id } });
        })
        .catch((e) => {
          console.log(e);
          errors.value = "Something went wrong";
        })
        .finally(() => {
          loading.value = false;
        });
      /*
      const data = {
        subject: `Migration for Client: ${app.user.client.id}`,
        body,
        priority_id: `${opts.priority.id}`,
        department_id: `${opts.departmentId}`,
      };

          body: data.reply || data.body,
    category_id: data.category,
    department_id: data.department,
    priority_id: data.priority,
    recipients: data.recipients,
    service_id: data.service,
    subject: data.subject,
    attachment_ids: data.attachments,
    ticket_form_field_1: data.phone,
    ticket_form_field_2: data.domain,

      */
    };

    onMounted(() => {
      if (!process.env.VUE_APP_SUPPORT_MIGRATION_DEPARTMENT) {
        root.$router.push({ name: "tickets" });
        return;
      }

      if (!store.getters["tickets/getTicketOptions"]) {
        loadingSettings.value = true;
        store
          .dispatch("tickets/ticketOptions")
          .then(() => {})
          .catch(() => {})
          .finally(() => {
            loadingSettings.value = false;
          });
      }
    });

    return {
      // fields,
      step,
      errors,
      validation,
      loading,
      // siteArray,
      fields2,
      validation2,
      goToSite,
      goToContact,
      addSiteAndConfirm,
      addSite,
      // siteIndex,
      test,
      getBody,
      loadSite,
      deleteSite,
      migration,
      goToLastSite,
      // getLines,
      getSummaryInfo,
      submitTicket,
      loadingSettings,
      normalTicketPriority,
      clearErrors,
    };
  },
};
</script>

<style lang="scss">
.migration-container {
  display: flex;

  .loading-container {
    width: 100%;
    padding: 2rem;
    display: flex;
    justify-content: center;
  }
  .migration-summary {
    padding: 1.5rem;
    width: 200px;
    border-width: 0 1px 0 0;
    border-style: solid;

    .step-container {
      display: flex;
      margin-bottom: 1.5rem;

      .step-check {
        width: 25px;
        .inner {
          border-width: 1px;
          border-style: solid;
          border-radius: 1rem;
          width: 15px;
          height: 15px;
          margin-top: 6px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }

      .step-description {
        padding-top: 0.25rem;
        flex-grow: 1;

        .step-title {
          font-size: 0.875rem;
          font-weight: 500;
        }
        .step-subtitle {
          font-size: 0.75rem;
        }

        .step-site {
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          font-size: 0.75rem;
          border-style: solid;
          border-width: 1px 0 0;

          .site-edit {
            display: flex;
            margin-top: 0.5rem;

            a {
              margin-right: 1rem;
            }
          }
        }
      }
    }
  }

  .migration-body {
    // padding: 1.5rem;
    flex-grow: 1;

    .title {
      padding: 1rem 1.5rem;
      border-width: 0 0 1px 0;
      border-style: solid;
    }

    .content {
      padding: 1rem 1.5rem;

      .content-action {
        display: flex;
        justify-content: flex-end;
        margin-top: 2rem;
        margin-bottom: 1rem;
      }
    }

    .site-summary {
      font-size: 0.875rem;
      border-style: solid;
      border-width: 1px;
      padding: 1rem;

      .site-actions {
        display: flex;
        margin-top: 0.5rem;

        a {
          margin-right: 1rem;
        }
      }
    }
  }
}
</style>
